import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Group as GroupIcon,
  LocalOffer as CouponIcon
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';
import CustomerSelectionModal from '../components/marketing/CustomerSelectionModal';

interface Template {
  id: number;
  name: string;
  body: string;
  category: string;
}

interface Provider {
  _id: string;
  name: string;
  identifier: string;
  isDefault: boolean;
  apiConfig: {
    senderNames: Array<{ name: string; isDefault: boolean; isActive: boolean }>;
  };
}

interface Coupon {
  _id: string;
  code: string;
  description: string;
  discountValue: number;
  discountType: 'percentage' | 'fixed';
}

const SmsSendPage: React.FC = () => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [currentRecipient, setCurrentRecipient] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sender, setSender] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [availableSenders, setAvailableSenders] = useState<string[]>([]);
  const [messageType, setMessageType] = useState('custom');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [messageCount, setMessageCount] = useState(1);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [sendingProgress, setSendingProgress] = useState<{
    show: boolean;
    current: number;
    total: number;
    message: string;
  }>({ show: false, current: 0, total: 0, message: '' });

  // حالات الكوبونات الفريدة
  const [enableUniqueCoupons, setEnableUniqueCoupons] = useState(false);
  const [baseCouponId, setBaseCouponId] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  // تحميل المزودين والقوالب
  useEffect(() => {
    loadProviders();
    loadTemplates();
    loadAvailableCoupons();
  }, []);

  // تحديث أسماء المرسلين عند تغيير المزود
  useEffect(() => {
    if (selectedProvider) {
      const provider = providers.find(p => p._id === selectedProvider);
      if (provider) {
        const senders = provider.apiConfig.senderNames
          .filter(s => s.isActive)
          .map(s => s.name);
        setAvailableSenders(senders);
        
        // تعيين المرسل الافتراضي
        const defaultSender = provider.apiConfig.senderNames.find(s => s.isDefault && s.isActive);
        if (defaultSender) {
          setSender(defaultSender.name);
        } else if (senders.length > 0) {
          setSender(senders[0]);
        }
      }
    }
  }, [selectedProvider, providers]);

  // حساب طول الرسالة وعدد الرسائل
  useEffect(() => {
    const length = messageBody.length;
    setMessageLength(length);
    setMessageCount(Math.ceil(length / 70) || 1);
    
    // حساب التكلفة التقريبية (0.026 ريال للرسالة الواحدة)
    const cost = Math.ceil(length / 70) * recipients.length * 0.026;
    setEstimatedCost(cost);
  }, [messageBody, recipients]);

  const loadProviders = async () => {
    try {
      const response = await axios.get('/marketing/sms/api/providers/active');
      if (response.data.success) {
        const providersList = response.data.data.providers;
        setProviders(providersList);
        
        // تعيين المزود الافتراضي
        const defaultProvider = providersList.find((p: Provider) => p.isDefault);
        if (defaultProvider) {
          setSelectedProvider(defaultProvider._id);
        } else if (providersList.length > 0) {
          setSelectedProvider(providersList[0]._id);
        }
      }
    } catch (err) {
      console.error('خطأ في تحميل المزودين:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get('/marketing/sms/api/templates');
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (err) {
      console.error('خطأ في تحميل القوالب:', err);
    }
  };

  const loadAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await axios.get('/coupons');
      if (response.data.success) {
        setAvailableCoupons(response.data.data.coupons || []);
      }
    } catch (err) {
      console.error('خطأ في تحميل الكوبونات:', err);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleAddRecipient = () => {
    if (currentRecipient.trim() === '') return;
    
    // تنظيف الرقم
    let cleanNumber = currentRecipient.replace(/[\s\-\+]/g, '');
    
    // التحقق من صحة الرقم
    if (!/^(966|05)[0-9]{8,9}$/.test(cleanNumber)) {
      setError('رقم الهاتف غير صحيح. يجب أن يبدأ بـ 966 أو 05');
      return;
    }
    
    // تحويل إلى الصيغة الدولية
    if (cleanNumber.startsWith('05')) {
      cleanNumber = '966' + cleanNumber.substring(1);
    }
    
    if (!recipients.includes(cleanNumber)) {
      setRecipients([...recipients, cleanNumber]);
      setCurrentRecipient('');
      setError(null);
    } else {
      setError('هذا الرقم موجود بالفعل');
    }
  };

  const handleAddCustomers = (phoneNumbers: string[]) => {
    // إضافة الأرقام الجديدة مع تجنب التكرار
    const uniqueNumbers = phoneNumbers.filter(num => !recipients.includes(num));
    if (uniqueNumbers.length > 0) {
      setRecipients([...recipients, ...uniqueNumbers]);
      setSuccess(`تم إضافة ${uniqueNumbers.length} رقم من العملاء`);
    } else {
      setError('جميع الأرقام المختارة موجودة بالفعل');
    }
  };

  const handleRemoveRecipient = (number: string) => {
    setRecipients(recipients.filter(r => r !== number));
  };

  const handleUseTemplate = (template: Template) => {
    setMessageBody(template.body);
    setShowTemplates(false);
  };

  const handleSendMessage = async () => {
    // التحقق من البيانات
    if (recipients.length === 0) {
      setError('يجب إضافة مستلم واحد على الأقل');
      return;
    }
    
    if (messageBody.trim() === '') {
      setError('يجب إدخال محتوى الرسالة');
      return;
    }
    
    if (isScheduled && !scheduledDateTime) {
      setError('يجب تحديد تاريخ ووقت الإرسال');
      return;
    }
    
    // التحقق من اختيار الكوبون إذا كان مفعلاً
    if (enableUniqueCoupons && !baseCouponId) {
      setError('يجب اختيار كوبون النموذج عند تفعيل الكوبونات الفريدة');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let endpoint = isScheduled ? '/marketing/sms/api/schedule' : '/marketing/sms/api/send';
      let data: any = {
        recipients,
        body: messageBody,
        sender: sender || undefined,
        providerId: selectedProvider || undefined,
        messageType
      };
      
      // إذا كانت الكوبونات الفريدة مفعلة، نستخدم المسار الجديد
      if (enableUniqueCoupons && baseCouponId) {
        endpoint = '/marketing/sms/api/send-with-coupons';
        data.baseCouponId = baseCouponId;
      }
      
      if (isScheduled) {
        data.scheduledDateTime = scheduledDateTime;
      }
      
      const response = await axios.post(endpoint, data);
      
      if (response.data.success) {
        // التحقق من وجود دفعات متعددة
        if (response.data.data?.totalBatches && response.data.data.totalBatches > 1) {
          const summary = response.data.data.summary;
          let successMessage = 
            `${response.data.message}\n` +
            `✅ تم الإرسال: ${summary.totalSent} مستلم\n` +
            `📦 عدد الدفعات: ${response.data.data.totalBatches}\n` +
            `💰 التكلفة: ${summary.totalCost.toFixed(3)} ريال`;
          
          // إضافة معلومات الكوبونات إذا كانت موجودة
          if (response.data.data.coupons && response.data.data.coupons.length > 0) {
            successMessage += `\n🎁 تم إنشاء ${response.data.data.coupons.length} كوبون فريد`;
          }
          
          setSuccess(successMessage);
        } else {
          let successMessage = response.data.message;
          
          // إضافة معلومات الكوبونات إذا كانت موجودة
          if (response.data.data?.coupons && response.data.data.coupons.length > 0) {
            successMessage += `\n🎁 تم إنشاء ${response.data.data.coupons.length} كوبون فريد`;
          }
          
          setSuccess(successMessage);
        }
        
        // إعادة تعيين النموذج
        setRecipients([]);
        setMessageBody('');
        setIsScheduled(false);
        setScheduledDateTime(null);
        setEnableUniqueCoupons(false);
        setBaseCouponId('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء إرسال الرسالة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            إرسال رسائل SMS
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)} style={{ whiteSpace: 'pre-line' }}>
              {success}
            </Alert>
          )}

          {sendingProgress.show && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" gutterBottom>
                  {sendingProgress.message}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    الدفعة {sendingProgress.current} من {sendingProgress.total}
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* قسم المستلمين */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  المستلمون ({recipients.length})
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    placeholder="966500000000 أو 0500000000"
                    value={currentRecipient}
                    onChange={(e) => setCurrentRecipient(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddRecipient();
                      }
                    }}
                    dir="ltr"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddRecipient}
                    startIcon={<AddIcon />}
                  >
                    إضافة
                  </Button>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowCustomerModal(true)}
                    startIcon={<GroupIcon />}
                  >
                    إضافة من العملاء
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '200px', overflowY: 'auto' }}>
                  {recipients.map((number, index) => (
                    <Chip
                      key={`${number}-${index}`}
                      label={number}
                      onDelete={() => handleRemoveRecipient(number)}
                      deleteIcon={<DeleteIcon />}
                      color="primary"
                      variant="outlined"
                      dir="ltr"
                    />
                  ))}
                </Box>

                {recipients.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    لم يتم إضافة أي مستلمين بعد
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* قسم الرسالة */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    محتوى الرسالة
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowTemplates(true)}
                    startIcon={<CopyIcon />}
                  >
                    استخدام قالب
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="نص الرسالة"
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="اكتب رسالتك هنا... يمكنك استخدام {كود_الخصم} كمرشح"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    الطول: {messageLength} حرف
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    عدد الرسائل: {messageCount}
                  </Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>نوع الرسالة</InputLabel>
                  <Select
                    value={messageType}
                    label="نوع الرسالة"
                    onChange={(e) => setMessageType(e.target.value)}
                  >
                    <MenuItem value="custom">رسالة عادية</MenuItem>
                    <MenuItem value="promotional">رسالة ترويجية</MenuItem>
                    <MenuItem value="transactional">رسالة معاملات</MenuItem>
                    <MenuItem value="notification">إشعار</MenuItem>
                    <MenuItem value="reminder">تذكير</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>مزود الخدمة</InputLabel>
                  <Select
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    label="مزود الخدمة"
                  >
                    {providers.map((provider) => (
                      <MenuItem key={provider._id} value={provider._id}>
                        {provider.name} {provider.isDefault && '(افتراضي)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>اسم المرسل</InputLabel>
                  <Select
                    value={sender}
                    onChange={(e) => setSender(e.target.value)}
                    label="اسم المرسل"
                    disabled={availableSenders.length === 0}
                  >
                    {availableSenders.map((senderName) => (
                      <MenuItem key={senderName} value={senderName}>
                        {senderName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            {/* قسم الكوبونات الفريدة */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CouponIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    خاصية الكوبونات الفريدة
                  </Typography>
                </Box>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableUniqueCoupons}
                      onChange={(e) => setEnableUniqueCoupons(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="إنشاء كوبون فريد لكل رقم مرسل (6-7 أرقام)"
                />
                
                {enableUniqueCoupons && (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>اختر كوبون النموذج</InputLabel>
                    <Select
                      value={baseCouponId}
                      onChange={(e) => setBaseCouponId(e.target.value)}
                      label="اختر كوبون النموذج"
                      disabled={loadingCoupons}
                    >
                      {availableCoupons.map((coupon) => (
                        <MenuItem key={coupon._id} value={coupon._id}>
                          {coupon.code} - {coupon.discountValue}
                          {coupon.discountType === 'percentage' ? '%' : ' ريال'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {enableUniqueCoupons && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    سيتم إنشاء كوبون فريد مكوّن من 6-7 أرقام لكل رقم هاتف في القائمة.
                    استخدم <strong>{'{كود_الخصم}'}</strong> في نص الرسالة لإضافة الكود تلقائياً.
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* قسم الجدولة والإرسال */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>وقت الإرسال</InputLabel>
                        <Select
                          value={isScheduled ? 'scheduled' : 'now'}
                          label="وقت الإرسال"
                          onChange={(e) => setIsScheduled(e.target.value === 'scheduled')}
                        >
                          <MenuItem value="now">إرسال فوري</MenuItem>
                          <MenuItem value="scheduled">جدولة الإرسال</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {isScheduled && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="تاريخ ووقت الإرسال"
                          type="datetime-local"
                          value={scheduledDateTime ? new Date(scheduledDateTime.getTime() - scheduledDateTime.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setScheduledDateTime(e.target.value ? new Date(e.target.value) : null)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={isScheduled ? 4 : 8}>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Tooltip title={`التكلفة التقريبية: ${estimatedCost.toFixed(3)} ريال`}>
                          <IconButton color="info">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleSendMessage}
                          disabled={loading || recipients.length === 0 || !messageBody}
                          startIcon={loading ? <CircularProgress size={20} /> : (isScheduled ? <ScheduleIcon /> : <SendIcon />)}
                        >
                          {loading ? 'جاري الإرسال...' : (isScheduled ? 'جدولة الرسالة' : 'إرسال الآن')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        عدد المستلمين
                      </Typography>
                      <Typography variant="h6">
                        {recipients.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        عدد الرسائل
                      </Typography>
                      <Typography variant="h6">
                        {messageCount * recipients.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        التكلفة التقريبية
                      </Typography>
                      <Typography variant="h6">
                        {estimatedCost.toFixed(3)} ريال
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        نوع الإرسال
                      </Typography>
                      <Typography variant="h6">
                        {isScheduled ? 'مجدول' : 'فوري'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* نافذة القوالب */}
          <Dialog
            open={showTemplates}
            onClose={() => setShowTemplates(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>اختر قالب رسالة</DialogTitle>
            <DialogContent>
              <List>
                {templates.map((template) => (
                  <ListItem
                    key={template.id}
                    button
                    onClick={() => handleUseTemplate(template)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={template.name}
                      secondary={template.body}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTemplates(false)}>
                إلغاء
              </Button>
            </DialogActions>
          </Dialog>

          {/* نافذة اختيار العملاء */}
          <CustomerSelectionModal
            open={showCustomerModal}
            onClose={() => setShowCustomerModal(false)}
            onSelectCustomers={handleAddCustomers}
            existingRecipients={recipients}
          />
        </Box>
      </Container>
  );
};

export default SmsSendPage;